import { OnImageSaveProps } from '@components/form-builder/form-builder';
import { FormV2 } from '@components/form/v2';

import { FormTemplateImageResponse } from 'api/forms/requests';
import { request } from 'api/request';

export type JourneyForm = {
  description: string;
  fieldsConfig: FormV2;
  isPublished: boolean;
  isAutoStart: boolean;
  isPortalForm: boolean;
  journey: string;
  journeyUuid: string;
  name: string;
  shortId: string;
  submissionCount: number;
  submissionMessageConfig: FormV2;
  uuid: string;
  workspace: string;
  portalDescription: string;
  portalImageUrl: string;
  portals: {
    shortId: string;
    name: string;
    slug: string;
  }[];
};

export type PatchPortalInfoType = Pick<JourneyForm, 'portalDescription'> & {
  portalImage?: File;
  formName?: string;
};

export const loadJourneyForms = async (journeyId: string) => {
  const response = await request.get<JourneyForm[]>(`v1/journey-forms/?journey_id=${journeyId}`);
  return response.data;
};

export const loadJourneyForm = async (journeyFormId: string) => {
  const response = await request.get<JourneyForm>(`v1/journey-forms/${journeyFormId}/`);
  return response.data;
};

export const deleteJourneyForm = async (journeyFormId: string) => {
  const response = await request.delete<void>(`v1/journey-forms/${journeyFormId}/`);
  return response.data;
};

export const publishJourneyForm = async (journeyFormId: string) => {
  const response = await request.post<void>(`v1/journey-forms/${journeyFormId}/publish`);
  return response.data;
};

export const unpublishJourneyForm = async (journeyFormId: string) => {
  const response = await request.post<void>(`v1/journey-forms/${journeyFormId}/unpublish`);
  return response.data;
};

export const duplicateJourneyForm = async (journeyFormId: string) => {
  const response = await request.post<void>(`v1/journey-forms/${journeyFormId}/duplicate`);
  return response.data;
};

export const createJourneyForm = async (journeyId: string, data: Partial<JourneyForm>) => {
  const response = await request.post<JourneyForm>(`v1/journey-forms/?journey_id=${journeyId}`, {
    ...data,
    journey: journeyId,
  });
  return response.data;
};

export const updateJourneyForm = async (journeyFormId: string, data: Partial<JourneyForm>) => {
  const response = await request.patch<JourneyForm>(`v1/journey-forms/${journeyFormId}/`, data);
  return response.data;
};

export const createFormImage = async ({ data, setProgress }: OnImageSaveProps) => {
  const response = await request.post<FormTemplateImageResponse>(`v1/journey-forms/image/`, data, {
    onUploadProgress: (progressEvent) => {
      if (setProgress) {
        const { loaded, total } = progressEvent;
        if (total) {
          setProgress(loaded / total);
        }
      }
    },
  });
  return response.data;
};

export type JourneyFormPortalOptions = {
  portals: {
    value: string;
    label: string;
  };
};

export const loadPortalsUsingJourneyForm = async (journeyFormId: string) => {
  const response = await request.get<JourneyFormPortalOptions>(
    `v1/journey-forms/${journeyFormId}/portal-info`
  );
  return response.data.portals;
};

export const updateJourneyFormPortalInfo = async (
  journeyFormId: string,
  data: PatchPortalInfoType
) => {
  const response = await request.postForm<JourneyForm>(
    `v1/journey-forms/${journeyFormId}/portal-info`,
    data
  );
  return response.data;
};
